package com.routexk.quiz.analytics

import kotlinx.browser.document
import kotlinx.browser.window

/**
 * Meta (Facebook) Pixel per RouteXK_FB_Pixel_Tech_Task_UA.md (repo root).
 *
 * Browser half of the dual browser-pixel + Conversions-API setup. The funnel
 * is a client-side SPA, so the standard <head> snippet's automatic PageView
 * would only cover the first document load — instead the snippet is injected
 * here without an initial PageView and every page composable fires
 * [pageView] on mount (exactly once per route, including the first).
 *
 * Deduplication with the server (CAPI) rides on shared event_ids:
 *  - Purchase / StartTrial: derived from the Stripe PaymentIntent id
 *    ([purchaseEventId] / [trialEventId]), so the webhook computes the same
 *    id with no coordination.
 *  - Lead / InitiateCheckout: generated client-side ([newEventId]) and stored
 *    in the backend session's custom_data together with the _fbp/_fbc cookies.
 *
 * Only the public Pixel ID lives here. The CAPI access token is backend-only
 * (FB_CAPI_ACCESS_TOKEN) and must never appear in this repo.
 */
object FbPixel {

    /** Public pixel identifier — visible in page source by design. */
    private const val PIXEL_ID = "2221707538672430"

    private var initialized = false

    /** Injects the fbevents.js stub + loader once. Safe to call repeatedly. */
    fun init() {
        if (initialized) return
        initialized = true
        js(
            "!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?" +
                "n.callMethod.apply(n,arguments):n.queue.push(arguments)};" +
                "if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';" +
                "n.queue=[];t=b.createElement(e);t.async=!0;" +
                "t.src=v;s=b.getElementsByTagName(e)[0];" +
                "s.parentNode.insertBefore(t,s)}(window,document,'script'," +
                "'https://connect.facebook.net/en_US/fbevents.js')"
        )
        window.asDynamic().fbq("init", PIXEL_ID)
    }

    /**
     * Re-inits the pixel with the visitor's email for Advanced Matching
     * (Facebook hashes it on its side). Called after the email is accepted
     * by the backend on /email.
     */
    fun setUserEmail(email: String) {
        init()
        val am = js("{}")
        am.em = email.trim().lowercase()
        window.asDynamic().fbq("init", PIXEL_ID, am)
    }

    /** Manual SPA page view — one call per route change. */
    fun pageView() {
        init()
        window.asDynamic().fbq("track", "PageView")
    }

    /**
     * Standard event. [eventId] enables dedup against the CAPI copy — required
     * for Purchase/StartTrial/Lead/InitiateCheckout. Null-valued params are
     * dropped so "no value" never turns into value: null/0 (Lead rule, §5.6).
     */
    fun track(name: String, eventId: String? = null, vararg params: Pair<String, Any?>) {
        init()
        if (eventId != null) {
            window.asDynamic().fbq("track", name, paramsObj(params), eventIdObj(eventId))
        } else {
            window.asDynamic().fbq("track", name, paramsObj(params))
        }
    }

    /** Custom (non-standard) event for retargeting/analytics audiences. */
    fun trackCustom(name: String, vararg params: Pair<String, Any?>) {
        init()
        window.asDynamic().fbq("trackCustom", name, paramsObj(params))
    }

    /** JS string array for content_ids. */
    fun ids(vararg values: String): dynamic {
        val arr = js("[]")
        values.forEach { arr.push(it) }
        return arr
    }

    private fun paramsObj(params: Array<out Pair<String, Any?>>): dynamic {
        val obj = js("{}")
        params.forEach { (key, value) -> if (value != null) obj[key] = value }
        return obj
    }

    private fun eventIdObj(eventId: String): dynamic {
        val obj = js("{}")
        obj.eventID = eventId
        return obj
    }

    // --- Shared event ids (browser + CAPI dedup) ---------------------------

    /** Both sides derive these from the Stripe PaymentIntent id. */
    fun purchaseEventId(transactionId: String) = "purch_$transactionId"
    fun trialEventId(transactionId: String) = "trial_$transactionId"

    /** Random id for events created browser-first (Lead, InitiateCheckout). */
    fun newEventId(prefix: String): String {
        val rnd = (1..4).joinToString("") {
            kotlin.random.Random.nextInt(0x10000).toString(16).padStart(4, '0')
        }
        return "${prefix}_$rnd"
    }

    // --- _fbp / _fbc cookies (match-quality boosters for CAPI) -------------

    /** Browser id cookie the pixel sets automatically. */
    fun fbp(): String? = cookie("_fbp")

    /**
     * Click id cookie (set when the visitor lands with ?fbclid=...). When the
     * cookie is missing but fbclid is in the URL, builds the documented
     * fallback fb.1.<timestamp_ms>.<fbclid>.
     */
    fun fbc(): String? {
        cookie("_fbc")?.let { return it }
        val fbclid = js("new URLSearchParams(window.location.search)").get("fbclid") as? String
        return fbclid?.takeIf { it.isNotBlank() }
            ?.let { "fb.1.${kotlin.js.Date.now().toLong()}.$it" }
    }

    private fun cookie(name: String): String? =
        document.cookie.split("; ")
            .firstOrNull { it.startsWith("$name=") }
            ?.substringAfter('=')
            ?.takeIf { it.isNotBlank() }

    // --- Funnel helpers ----------------------------------------------------

    /** True when the browser Purchase for this transaction was already sent. */
    fun purchaseAlreadySent(transactionId: String): Boolean =
        window.sessionStorage.getItem(purchaseSentKey(transactionId)) != null

    fun markPurchaseSent(transactionId: String) {
        window.sessionStorage.setItem(purchaseSentKey(transactionId), "1")
    }

    private fun purchaseSentKey(transactionId: String) = "fb_purchase_sent_$transactionId"
}
